import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  DragEndEvent,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arraySwap } from '@dnd-kit/sortable';
import { AdImage } from '@/components/AdImages/AdImages.types';
import { toBase64 } from '@/utils/helpers';
import { uuidShort } from '@/utils/uuid-generators';
import { compressPhoto } from '@/utils/image-comprassion';

export const useImagesList = (initialImages: AdImage[] = []) => {
  const [images, setImages] = useState<AdImage[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const sensors = useSensors(
    useSensor(TouchSensor, {
      activationConstraint: { delay: 100, tolerance: 5 },
    })
  );

  function triggerAdd() {
    fileInputRef.current?.click();
  }

  function onDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    if (over?.id && active.id !== over.id) {
      setImages(prev => {
        const oldIndex = prev.findIndex(el => el.id === active.id);
        const newIndex = prev.findIndex(el => el.id === over.id);
        return arraySwap(prev, oldIndex, newIndex);
      });
    }
  }

  const onRemove = useCallback((id: string) => {
    setImages(prev => prev.filter(el => el.id !== id));
  }, []);

  async function onFileInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { files } = e.target;
    if (!files) return;

    const compressedPromises = Array.from(files).map(async file => {
      const compressedFile = await compressPhoto(file, 950);
      return {
        id: uuidShort(),
        src: await toBase64(compressedFile || file),
      };
    });

    const compressedImages = await Promise.all(compressedPromises);

    const newImages = compressedImages.filter(Boolean);

    setImages(prev => [...prev, ...newImages]);
  }

  useEffect(() => {
    setImages(initialImages);
  }, []);

  return {
    images,
    setImages,
    sensors,
    onDragEnd,
    onRemove,
    onFileInputChange,
    fileInputRef,
    triggerAdd,
  };
};
